import React from 'react';
import Image from 'next/image';
import { Button } from 'antd';
import Utils from '../../../../utils/Utils';
import { JobCardUiv2Container } from './JobCardUIV2Container';
import { FeaturedTag } from '../../FeaturedTag';
import { JobMetaItem } from './JobMetaItem';
import LocationPinIcon from '../../../icons/LocationPinIcon';
import CurrencyDollarIcon from '../../../icons/CurrencyDollarIcon';
import CalendarIcon from '../../../icons/CalendarIcon';
import BookmarkIcon from '../../../icons/BookmarkIcon';

export type JobCardV2Job = {
  title: string;
  company: string;
  postedDate: string;
  location: string;
  salary: string;
  daysRemaining: string;
  featured?: boolean;
  quickApply?: boolean;
  logo: string;
  logoBg?: string;
};

type JobCardV2Props = {
  job: JobCardV2Job;
  className?: string;
  actionLabel?: string;
  onApply?: (job: JobCardV2Job) => void;
};

export const JobCardV2: React.FC<JobCardV2Props> = ({
  job,
  className,
  actionLabel = 'Apply Now',
  onApply,
}) => {
  const handleApply = () => {
    onApply?.(job);
  };

  return (
    <JobCardUiv2Container
      className={Utils.cn('items-start justify-between bg-home-card', className)}>
      <div className="flex flex-1 items-start gap-3 pr-4">
        <div
          className={Utils.cn(
            'inline-flex w-14 h-14 items-start justify-start rounded p-4',
            job.logoBg,
          )}>
          <Image
            src={job.logo}
            alt={`${job.title} logo`}
            width={24}
            height={24}
            className="w-6 h-6 self-start object-contain"
            unoptimized
          />
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="font-sans text-[16px] leading-[30px] font-semibold text-midnight-900">
              {job.title}
            </span>
            {job.featured && <FeaturedTag />}
          </div>
          <div className="flex items-center gap-[8px]">
            <span className="font-sans font-medium text-[16px] leading-[28px] text-gray-600">
              {job.company}
            </span>
            <span className="w-[5px] h-[5px] rounded-full bg-gray-400" aria-hidden="true" />
            <span className="font-sans font-medium text-[16px] leading-[28px] text-gray-600">
              {job.postedDate}
            </span>
            {job.quickApply && (
              <>
                <span className="w-[5px] h-[5px] rounded-full bg-gray-400" aria-hidden="true" />
                <span className="font-sans font-medium text-[16px] leading-[28px] text-ai-purple">
                  Quick Apply
                </span>
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <JobMetaItem
              icon={<LocationPinIcon className="w-[22px] h-[22px]" />}
              label={job.location}
            />
            <JobMetaItem
              icon={<CurrencyDollarIcon className="w-[22px] h-[22px]" />}
              label={job.salary}
            />
            <JobMetaItem
              icon={<CalendarIcon className="w-[22px] h-[22px]" />}
              label={job.daysRemaining}
            />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center rounded-lg border-none bg-brand-50">
          <BookmarkIcon className="w-5 h-5 text-ai-purple" />
        </button>
        <Button
          className="w-[120px] h-11 px-[18px] py-[10px] gap-2 rounded-lg border border-gray-300 shadow-[0px_1px_2px_rgba(10,13,18,0.05)] text-gray-700 font-semibold text-sm"
          onClick={handleApply}>
          {actionLabel}
        </Button>
      </div>
    </JobCardUiv2Container>
  );
};

JobCardV2.displayName = 'JobCardV2';

export default JobCardV2;
